import { useState, useMemo, useCallback } from 'react';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';
import { VoxRoi } from './VoxRoi';
import VoxRoiCostRail from './VoxRoiCostRail';
import { useVoxRoiConfig, useVoxRoiCatalog, useVoxRoiUsage, useUpdateVoxRoiConfig } from '../../hooks/useVoxRoi';
import { decryptConfigBlob, encryptConfigBlob, MalformedBlobError } from '../../utils/crypto';
import { computeRoi } from '../../utils/voxRoiCalc';
import { VoxRoiCostConfigData, VOX_ROI_CONFIG_VERSION, CostScheduleEntry, BeforeProviderEntry, VoxRoiEditSection } from '../../models/voxRoi';

type ParsedConfig =
  | { ok: true; config: VoxRoiCostConfigData }
  | { ok: false; reason: 'version' | 'invalid' };

const isCostScheduleEntry = (entry: unknown): entry is CostScheduleEntry => {
  if (typeof entry !== 'object' || entry === null) return false;
  const candidate = entry as Record<string, unknown>;
  return (
    typeof candidate.effectiveFrom === 'string' &&
    (candidate.effectiveTo === null || typeof candidate.effectiveTo === 'string') &&
    (candidate.cost === null || (typeof candidate.cost === 'number' && Number.isFinite(candidate.cost)))
  );
};

const isBeforeProviderEntry = (entry: unknown): entry is BeforeProviderEntry => {
  if (typeof entry !== 'object' || entry === null) return false;
  const candidate = entry as Record<string, unknown>;
  return (
    typeof candidate.ordersPerLoan === 'number' &&
    Number.isFinite(candidate.ordersPerLoan) &&
    typeof candidate.cost === 'number' &&
    Number.isFinite(candidate.cost)
  );
};

const isCostScheduleMap = (value: unknown): value is Record<string, CostScheduleEntry[]> => {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every(
    (schedule) => Array.isArray(schedule) && schedule.every(isCostScheduleEntry),
  );
};

const isBeforeProviderMap = (value: unknown): value is Record<string, BeforeProviderEntry> => {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value).every(isBeforeProviderEntry);
};

/** Sort each product's schedule entries by effectiveFrom ascending (once). */
const normalizeScheduleOrder = (config: VoxRoiCostConfigData): VoxRoiCostConfigData => {
  const sorted: Record<string, CostScheduleEntry[]> = {};
  for (const [key, schedule] of Object.entries(config.costs)) {
    sorted[key] = [...schedule].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  }
  return { ...config, costs: sorted };
};

/**
 * Parse and validate a decrypted config blob. v3 is the only supported version: v1/v2/unknown
 * shapes are rejected outright so we never silently reinterpret or migrate a legacy blob.
 */
export const parseAndValidateConfig = (plaintext: string): ParsedConfig => {
  let raw: unknown;
  try {
    raw = JSON.parse(plaintext);
  } catch {
    return { ok: false, reason: 'invalid' };
  }
  if (typeof raw !== 'object' || raw === null) return { ok: false, reason: 'invalid' };

  const candidate = raw as Record<string, unknown>;
  if (candidate.version !== VOX_ROI_CONFIG_VERSION) return { ok: false, reason: 'version' };
  if (!isCostScheduleMap(candidate.costs)) return { ok: false, reason: 'invalid' };
  if (!isBeforeProviderMap(candidate.beforeProviders)) return { ok: false, reason: 'invalid' };

  return {
    ok: true,
    config: normalizeScheduleOrder({
      version: VOX_ROI_CONFIG_VERSION,
      costs: candidate.costs,
      beforeProviders: candidate.beforeProviders,
    }),
  };
};

const defaultDateRange = (): Date[] => {
  const now = new Date();
  return [startOfDay(subDays(now, 30)), endOfDay(now)];
};

const VoxRoiContainer = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [decryptedConfig, setDecryptedConfig] = useState<VoxRoiCostConfigData | null>(null);
  const [dateRange, setDateRange] = useState<Date[] | undefined>(defaultDateRange);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeEditSection, setActiveEditSection] = useState<VoxRoiEditSection>(null);

  const configQuery = useVoxRoiConfig();
  const catalogQuery = useVoxRoiCatalog();
  const updateConfigMutation = useUpdateVoxRoiConfig();

  const startDate = dateRange?.[0] ? format(dateRange[0], 'yyyy-MM-dd') : undefined;
  const endDate = dateRange?.[1] ? format(dateRange[1], 'yyyy-MM-dd') : undefined;
  const usageQuery = useVoxRoiUsage({
    startDate,
    endDate,
    enabled: isUnlocked,
  });

  const configSettled = configQuery.isSuccess || configQuery.isError;
  const hasConfig = !!configQuery.data;
  const configLoadError = configQuery.isError;
  const canChangePassphrase = hasConfig;

  const handleUnlock = useCallback(async () => {
    setUnlockError(null);
    if (!configSettled) {
      setUnlockError('Still loading configuration, please wait.');
      return;
    }
    if (configQuery.isError) {
      setUnlockError('Failed to load configuration. Please try again.');
      return;
    }
    if (!passphrase.trim()) {
      setUnlockError('Please enter a passphrase.');
      return;
    }

    if (!configQuery.data) {
      if (passphrase !== confirmPassphrase) {
        setUnlockError('Passphrases do not match.');
        return;
      }
      setDecryptedConfig({ version: VOX_ROI_CONFIG_VERSION, costs: {}, beforeProviders: {} });
      setIsUnlocked(true);
      return;
    }

    let plaintext: string;
    try {
      plaintext = await decryptConfigBlob({ blob: configQuery.data.encryptedData, passphrase });
    } catch (error) {
      if (error instanceof MalformedBlobError) {
        setUnlockError('Configuration data is invalid and cannot be opened.');
        return;
      }
      setUnlockError('Incorrect passphrase.');
      return;
    }

    const parsed = parseAndValidateConfig(plaintext);
    if (!parsed.ok) {
      setUnlockError(
        parsed.reason === 'version'
          ? 'This configuration was saved in an unsupported format and cannot be opened here.'
          : 'Configuration data is invalid and cannot be opened.',
      );
      return;
    }

    setDecryptedConfig(parsed.config);
    setIsUnlocked(true);
  }, [passphrase, confirmPassphrase, configQuery.data, configQuery.isError, configSettled]);

  const result = useMemo(() => {
    if (!decryptedConfig || !catalogQuery.data || !usageQuery.data) return null;
    return computeRoi({ config: decryptedConfig, catalog: catalogQuery.data, usage: usageQuery.data });
  }, [decryptedConfig, catalogQuery.data, usageQuery.data]);

  const handleSaveConfig = useCallback(async (updatedConfig: VoxRoiCostConfigData): Promise<boolean> => {
    setSaveError(null);
    try {
      const encrypted = await encryptConfigBlob({ plaintext: JSON.stringify(updatedConfig), passphrase });
      await updateConfigMutation.mutateAsync({ encryptedData: encrypted, version: updatedConfig.version });
      setDecryptedConfig(updatedConfig);
      setActiveEditSection(null);
      return true;
    } catch {
      setSaveError('Failed to save configuration. Your last saved report is still shown.');
      return false;
    }
  }, [passphrase, updateConfigMutation]);

  const handleChangePassphrase = useCallback(async ({ newPassphrase }: { newPassphrase: string }) => {
    if (!decryptedConfig || !canChangePassphrase) {
      throw new Error('Cannot change passphrase before configuration is saved.');
    }
    const encrypted = await encryptConfigBlob({ plaintext: JSON.stringify(decryptedConfig), passphrase: newPassphrase });
    await updateConfigMutation.mutateAsync({ encryptedData: encrypted, version: decryptedConfig.version });
    setPassphrase(newPassphrase);
  }, [decryptedConfig, canChangePassphrase, updateConfigMutation]);

  return (
    <VoxRoi
      status={isUnlocked ? 'unlocked' : 'locked'}
      hasConfig={hasConfig}
      configLoadError={configLoadError}
      isConfigFetching={configQuery.isFetching}
      onRetryConfig={() => { void configQuery.refetch(); }}
      passphrase={passphrase}
      onPassphraseChange={setPassphrase}
      confirmPassphrase={confirmPassphrase}
      onConfirmPassphraseChange={setConfirmPassphrase}
      onUnlock={handleUnlock}
      unlockError={unlockError}
      isConfigLoading={!configSettled}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      result={result}
      isUsageLoading={usageQuery.isLoading}
      isCatalogLoading={catalogQuery.isLoading}
      isUsageFetching={usageQuery.isFetching}
      usageError={usageQuery.isError}
      catalogError={catalogQuery.isError}
      isCatalogFetching={catalogQuery.isFetching}
      onRetryUsage={() => { void usageQuery.refetch(); }}
      onRetryCatalog={() => { void catalogQuery.refetch(); }}
      costRail={
        isUnlocked && decryptedConfig && catalogQuery.data ? (
          <VoxRoiCostRail
            config={decryptedConfig}
            catalog={catalogQuery.data}
            onSave={handleSaveConfig}
            isSaving={updateConfigMutation.isPending}
            saveError={saveError}
            activeEditSection={activeEditSection}
            onEditSection={setActiveEditSection}
            onChangePassphrase={handleChangePassphrase}
            canChangePassphrase={canChangePassphrase}
          />
        ) : undefined
      }
    />
  );
};

export default VoxRoiContainer;
